import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material';
import { Maintenance, MaintenanceDeadlineContract } from '@core/interfaces';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { DeadlinesContractsMapper } from 'app/main/settings/registers/deadlines-contracts/mappers/deadlines-contracts.mapper';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import english from '../../../../i18n/en.json';
import spanish from '../../../../i18n/es.json';
import portuguese from '../../../../i18n/pt.json';
import { MaintenanceDialogData } from '../../dialog-maintenances.component';

@Component({
  selector: 'tab-new-deadline',
  templateUrl: './tab-new-deadline.component.html',
  styleUrls: ['./tab-new-deadline.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TabNewDeadlineComponent implements OnInit, OnDestroy {
  public maintenance: Maintenance;
  public maintenanceDeadlineContract: MaintenanceDeadlineContract;

  public customInterval: Record<string, any>[] = [];
  public newDeadlineForm: FormGroup;
  private subscriptions = new Subject();

  public isAllowToEditResponseViolationReason = false;
  public isAllowToEditSolutionViolationReason = false;

  @Output()
  public getDeadlinesForm: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MaintenanceDialogData,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly formBuilder: FormBuilder,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
    this.customInterval = [];
    this.maintenance = data.maintenance;
    this.maintenanceDeadlineContract = data.maintenance.deadlineContract;

    this.newDeadlineForm = this.formBuilder.group({
      responseDeadlineViolationReason: [null],
      solutionDeadlineViolationReason: [null],
    });
  }

  public ngOnInit(): void {
    const currentCustomInterval = this.maintenanceDeadlineContract && this.maintenanceDeadlineContract.customInterval;
    if (currentCustomInterval) {
      for (const day of Object.values(DeadlinesContractsMapper.daysOfWeek)) {
        const value = currentCustomInterval[day];
        this.customInterval.push({
          day: day.toUpperCase(),
          start: value && value.start && DeadlinesContractsMapper.toTimeWithTimezone(value.start),
          end: value && value.end && DeadlinesContractsMapper.toTimeWithTimezone(value.end),
        });
      }
    }

    if (this.maintenanceDeadlineContract) {
      const { responseDeadlineViolationReason, solutionDeadlineViolationReason } = this.maintenanceDeadlineContract;
      if (responseDeadlineViolationReason) {
        this.newDeadlineForm.get('responseDeadlineViolationReason').setValue(responseDeadlineViolationReason.deadlineViolationReason);
      }

      if (solutionDeadlineViolationReason) {
        this.newDeadlineForm.get('solutionDeadlineViolationReason').setValue(solutionDeadlineViolationReason.deadlineViolationReason);
      }

      const isResponseDeadlineViolatedOrCompletedWithViolation = ['violated', 'completedWithViolation'].includes(this.maintenance.responseDeadlineStatus);
      const isSolutionDeadlineViolatedOrCompletedWithViolation = ['violated', 'completedWithViolation'].includes(this.maintenance.solutionDeadlineStatus);

      this.isAllowToEditResponseViolationReason = isResponseDeadlineViolatedOrCompletedWithViolation && (!responseDeadlineViolationReason || responseDeadlineViolationReason.linkedBy === 'enterprise');
      this.isAllowToEditSolutionViolationReason = isSolutionDeadlineViolatedOrCompletedWithViolation && (!solutionDeadlineViolationReason || solutionDeadlineViolationReason.linkedBy === 'enterprise');
    }

    this.newDeadlineForm.valueChanges
      .pipe(takeUntil(this.subscriptions))
      .subscribe(() => {
        this.getDeadlinesForm.emit(this.newDeadlineForm);
      });

    this.getDeadlinesForm.emit(this.newDeadlineForm);
  }

  public ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.complete();
  }
}
